/**
 * Persists the visibility-filter selection (Set of visible line keys) for the
 * Visibility chart to localStorage, keyed per campaign (and optionally per
 * prompt).  Survives page reloads and return visits.
 *
 * Storage key format:
 *   aivis_vis_filter_c{campaignId}           (Dashboard)
 *   aivis_vis_filter_c{campaignId}_p{promptId}  (Prompt Detail)
 *
 * Stored value: JSON array of selected keys.
 *   ["__all__"]  → user explicitly selected everything (show all)
 *   [key, ...]   → specific selection
 *   absent       → never configured for this campaign → default to client-only
 *
 * State values:
 *   undefined  → no preference stored yet (will default to client-only on first lineOrder)
 *   null       → user explicitly selected all (show all)
 *   Set        → specific selection
 */
import { useCallback, useEffect, useRef, useState } from 'react';

const LS_PREFIX = 'aivis_vis_filter_v2_';
const ALL_SENTINEL = '__all__';

function storageKey(campaignId, promptId) {
  if (!campaignId) return null;
  return promptId
    ? `${LS_PREFIX}c${campaignId}_p${promptId}`
    : `${LS_PREFIX}c${campaignId}`;
}

/**
 * Returns:
 *   undefined  – key absent in storage (never set)
 *   null       – stored as "__all__" (user explicitly chose all)
 *   Set        – stored specific selection
 */
function readFromStorage(key) {
  if (!key) return undefined;
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return undefined; // never set
    const arr = JSON.parse(raw);
    if (!Array.isArray(arr) || arr.length === 0) return undefined;
    if (arr[0] === ALL_SENTINEL) return null; // explicit "show all"
    return new Set(arr);
  } catch {
    return undefined;
  }
}

function writeToStorage(key, value) {
  if (!key) return;
  try {
    if (value === null) {
      // Explicitly "show all" — store sentinel so we don't re-apply the default
      localStorage.setItem(key, JSON.stringify([ALL_SENTINEL]));
    } else if (value === undefined) {
      localStorage.removeItem(key);
    } else {
      localStorage.setItem(key, JSON.stringify([...value]));
    }
  } catch {
    // localStorage may be unavailable (private browsing quota exceeded etc.)
  }
}

/**
 * @param {string|number|null} campaignId
 * @param {string|number|null} [promptId]
 * @param {string[]}           lineOrder  - full list of line keys from the API
 *
 * Returns [visibleLines, setVisibleLines]:
 *   visibleLines — Set<string> of visible keys, or null (= all visible)
 *   setVisibleLines — setter; automatically persists to localStorage
 */
export function usePersistedVisibleLines(campaignId, promptId, lineOrder) {
  const key = storageKey(campaignId, promptId);

  // Initialize from localStorage on first render.
  // undefined = no preference yet; null = explicit all; Set = specific selection
  const [visibleLines, _setVisibleLines] = useState(() => readFromStorage(key));

  // Keep a ref to the current key so the setter always writes to the right slot
  const keyRef = useRef(key);
  useEffect(() => { keyRef.current = key; }, [key]);

  // When campaignId / promptId changes, reload from storage (or reset to undefined)
  const prevKeyRef = useRef(key);
  useEffect(() => {
    if (prevKeyRef.current === key) return;
    prevKeyRef.current = key;
    _setVisibleLines(readFromStorage(key));
  }, [key]);

  // Wrap setter so every call auto-persists
  const setVisibleLines = useCallback((valueOrUpdater) => {
    _setVisibleLines((prev) => {
      const next = typeof valueOrUpdater === 'function'
        ? valueOrUpdater(prev)
        : valueOrUpdater;
      writeToStorage(keyRef.current, next);
      return next;
    });
  }, []);

  // When lineOrder first arrives:
  //   • If no preference stored (undefined) and there are multiple lines → default to client-only
  //   • If preference stored → validate it; remove stale keys
  const lineOrderRef = useRef(lineOrder);
  useEffect(() => {
    const prev = lineOrderRef.current;
    lineOrderRef.current = lineOrder;
    if (!lineOrder?.length || prev === lineOrder) return;

    _setVisibleLines((current) => {
      // No saved preference yet → seed with client-only (first line = client)
      if (current === undefined) {
        if (lineOrder.length > 1) {
          const clientOnly = new Set([lineOrder[0]]);
          writeToStorage(keyRef.current, clientOnly);
          return clientOnly;
        }
        // Only one line — "show all" is equivalent; store explicit all so default isn't re-applied
        writeToStorage(keyRef.current, null);
        return null;
      }

      // Explicit "show all" — nothing to validate
      if (current === null) return current;

      // Validate stored selection against current lineOrder
      const valid = new Set([...current].filter((k) => lineOrder.includes(k)));
      if (valid.size === 0) {
        // All stored keys gone → fall back to client-only
        const clientOnly = new Set([lineOrder[0]]);
        writeToStorage(keyRef.current, clientOnly);
        return clientOnly;
      }
      if (valid.size === lineOrder.length) {
        // Everything is selected → treat as explicit "show all"
        writeToStorage(keyRef.current, null);
        return null;
      }
      if (valid.size !== current.size) {
        writeToStorage(keyRef.current, valid);
        return valid;
      }
      return current;
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lineOrder]);

  return [visibleLines, setVisibleLines];
}

/** Effective lines to render on the chart (undefined → client-only default). */
export function resolveVisibleLineOrder(lineOrder, visibleLines) {
  if (!lineOrder?.length) return [];
  if (visibleLines === null) return lineOrder;
  if (visibleLines === undefined) return lineOrder.length > 1 ? [lineOrder[0]] : lineOrder;
  const filtered = lineOrder.filter((k) => visibleLines.has(k));
  return filtered.length ? filtered : [lineOrder[0]];
}

export function isLineChecked(key, lineOrder, visibleLines) {
  if (visibleLines === null) return true;
  if (visibleLines === undefined) return key === lineOrder[0];
  return visibleLines.has(key);
}

export function isShowingAllLines(lineOrder, visibleLines) {
  if (!lineOrder?.length) return true;
  if (visibleLines === null) return true;
  if (visibleLines === undefined) return lineOrder.length <= 1;
  return visibleLines.size >= lineOrder.length;
}

export function getVisibleLineFilterCount(lineOrder, visibleLines) {
  return resolveVisibleLineOrder(lineOrder, visibleLines).length;
}

export function isVisibleLineFilterActive(lineOrder, visibleLines) {
  return !isShowingAllLines(lineOrder, visibleLines);
}

export function visibleLinesSelectionBase(lineOrder, visibleLines) {
  if (visibleLines === null) return new Set(lineOrder);
  if (visibleLines === undefined) return new Set([lineOrder[0]]);
  return new Set(visibleLines);
}
